/**
 * Iter XX.290 · XX.290-MIN — Second Innings · Session #1 success.
 *
 * Lands here after Stripe returns. URL: `?bid=<hex>&t=<hmac>`.
 * Polls booking status until `paid` (Stripe webhook confirms on the
 * server) and renders the confirmation card with the verbatim refund
 * disclosure.
 *
 * XX.290-MIN scope corrections:
 *   * Player Card form removed (DO NOT BUILD).
 *   * Single-slot rendering — reads proposed date/time/venue from
 *     the sessions doc via `/second-innings/session-1/config`.
 *   * Verbatim customer-facing refund disclosure rendered in every
 *     terminal state (`paid` and `pending`).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";

// XX.290-MIN — verbatim customer-facing refund disclosure. LOCKED
// WORDING; source of truth is the backend `SESSION_1_DISCLOSURE_TEXT`
// exposed via `/config`. This literal is a safe fallback for the
// brief window before `/config` resolves.
const DISCLOSURE_FALLBACK =
  "We're finalizing the booking for this session. "
  + "If Second Innings cannot confirm it, you receive a full $30 refund.";

export default function SecondInningsSession1Success() {
  const [params]  = useSearchParams();
  const bid = params.get("bid") || "";
  const t   = params.get("t") || "";
  const [booking, setBooking] = useState(null);
  const [disclosure, setDisclosure] = useState(DISCLOSURE_FALLBACK);
  const [phase, setPhase]     = useState("loading"); // loading|pending|paid|error
  const pollRef = useRef(0);

  useEffect(() => {
    // XX.291 — pull ONLY the disclosure text (locked wording) from
    // `/config`. Proposed date/venue are per-booking and come from
    // the booking GET response (which carries the session snapshot).
    let cancel = false;
    (async () => {
      try {
        const cfg = await api.get("/second-innings/session-1/config");
        if (!cancel && cfg.data?.disclosure_text) {
          setDisclosure(cfg.data.disclosure_text);
        }
      } catch { /* non-fatal — fallback constant is verbatim */ }
    })();
    return () => { cancel = true; };
  }, []);

  useEffect(() => {
    if (!bid || !t) { setPhase("error"); return; }
    let cancel = false;
    const poll = async () => {
      try {
        const { data } = await api.get(
          `/second-innings/session-1/bookings/${bid}?t=${encodeURIComponent(t)}`,
        );
        if (cancel) return;
        setBooking(data);
        if (data.status === "paid") {
          setPhase("paid");
        } else {
          setPhase("pending");
          pollRef.current += 1;
          if (pollRef.current < 20) {
            setTimeout(poll, 2000);
          }
        }
      } catch (e) {
        if (!cancel) setPhase("error");
      }
    };
    poll();
    return () => { cancel = true; };
  }, [bid, t]);

  const priceLabel = useMemo(() => {
    if (!booking) return "";
    return `${(booking.currency || "cad").toUpperCase()} $${(booking.amount_cents / 100).toFixed(0)}`;
  }, [booking]);

  const disclosureText = disclosure || DISCLOSURE_FALLBACK;
  const sess = booking?.session || null;
  const proposedDate = sess?.proposed_date_label || "TBD";
  const proposedTime = sess?.proposed_time_label || "TBD";
  const venueName    = sess?.venue_name || "TBD";
  const venueConfirmed = !!sess?.venue_confirmed;

  if (phase === "loading") {
    return <_Shell><p className="text-neutral-400">Confirming your payment…</p></_Shell>;
  }
  if (phase === "error") {
    return <_Shell>
      <_Err title="We couldn't load your booking">
        Please refresh, or reply to the Second Innings email so we can
        confirm your spot.
      </_Err>
    </_Shell>;
  }

  return (
    <_Shell>
      <header className="mb-6" data-testid="si-session1-success-header">
        <div className="text-[13px] uppercase tracking-[0.14em] text-emerald-300/80">
          Second Innings — Session #1
        </div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-neutral-50">
          {phase === "paid" ? "You're in." : "Almost there…"}
        </h1>
        {phase === "pending" && (
          <p className="mt-3 text-[14px] text-neutral-400">
            Waiting for Stripe to confirm your payment. This usually
            takes a few seconds.
          </p>
        )}
      </header>

      <section className="rounded-2xl border border-neutral-800 bg-neutral-950 p-5 sm:p-6 space-y-4">
        <div className="text-[13px] uppercase tracking-[0.14em] text-neutral-500">
          Proposed session
        </div>
        <div data-testid="si-session1-success-slot">
          <div className="text-neutral-100 text-[15px]">{proposedDate}</div>
          <div className="text-neutral-400 text-[13px] mt-0.5">
            {proposedTime} · America/Toronto
          </div>
          <div className="text-neutral-300 text-[13px] mt-2">
            <span className="text-neutral-500">Venue:</span>{" "}
            {venueName}
            {!venueConfirmed && (
              <span className="text-neutral-400"> (proposed — being finalized)</span>
            )}
          </div>
        </div>
        {phase === "paid" && priceLabel && (
          <div
            className="pt-3 border-t border-neutral-900 text-[13px] text-neutral-500"
            data-testid="si-session1-success-amount"
          >
            Paid <span className="text-neutral-200">{priceLabel}</span>
            {booking?.booking_id && (
              <>
                {" · "}Booking ref{" "}
                <code className="text-neutral-300">{booking.booking_id}</code>
              </>
            )}
          </div>
        )}
        {/* XX.290-MIN — verbatim customer-facing refund disclosure.
            LOCKED WORDING. Required by the paid-reservation contract:
            visible on the session page above the CTA, on this success
            page, and in the confirmation email. */}
        <p
          className="text-[13px] leading-relaxed text-neutral-300 pt-3 border-t border-neutral-900"
          data-testid="si-session1-success-disclosure"
        >
          {disclosureText}
        </p>
      </section>
    </_Shell>
  );
}

function _Shell({ children }) {
  return (
    <main className="min-h-screen bg-neutral-950 text-neutral-100">
      <div className="max-w-xl mx-auto px-4 sm:px-6 pt-10 pb-24">{children}</div>
    </main>
  );
}
function _Err({ title, children }) {
  return (
    <div className="rounded-2xl border border-red-900/40 bg-red-950/30 p-6">
      <div className="text-[13px] uppercase tracking-[0.14em] text-red-300/80">{title}</div>
      <p className="mt-3 text-[15px] text-neutral-200">{children}</p>
    </div>
  );
}
